import React, { useEffect, useState } from 'react';
import { Box, FormControl, Modal, VStack, WarningIcon } from 'native-base';
import { useGeneralStyles, useModalStyles } from './styles';
import { useI18n, useSpringBigStore } from '@springbig/shared';
import { Caption, H3 } from './Typography';
import Button from '~/shared/Button';
import EmailVerificationForm from './EmailVerification/EmailVerificationForm';
import { useOffersScreen } from '@springbig/shared';
import { useGetMember } from '@springbig/shared';
import dayjs from 'dayjs';
import MockGiftCard from '~/shared/MockGiftCard';

type ClaimGiftCardModalProps = {
    rewardGrantId: number;
    closeModal: () => void;
}

const ClaimGiftCardModal = ({
    rewardGrantId,
    closeModal
}: ClaimGiftCardModalProps) => {

    const { claimGiftCard } = useOffersScreen();
    const { walletId, member, setMember, merchant } = useSpringBigStore();
    const { refetch: refreshMember } = useGetMember();
    const giftCardGrant = member?.reward_grants?.find((r) => r.id === rewardGrantId);
    const { t } = useI18n();
    const gStyles = useGeneralStyles();
    const {
        text,
        body_background,
    } = useModalStyles();

    const handleClaimGiftCardPressed = () => {

        claimGiftCard.mutate({ reward_grant_id: rewardGrantId });
    };

    const handleSuccessCloseModalPressed = () => {

        refreshMember();
        closeModal();
    };

    const updateMember = (validatedEmail: string): void => {

        setMember({
            ...member,
            consumer: {
                ...member.consumer,
                email: validatedEmail,
                email_validated: true
            }
        });
    };

    const emailVerifyForm = (
        <VStack space={4}>
            <H3 color={text} textAlign={'center'} maxW={'80%'}>
                { t('giftCard.claimEmailVerifyHeader', [merchant.gift_card_alias]) }
            </H3>

            <EmailVerificationForm
                t={t}
                walletId={walletId}
                onCancel={closeModal}
                onProceed={updateMember}
            />
        </VStack>
    );

    const claimGiftCardForm = (
        <VStack space={4}>
            <Box>
                <H3 color={text}>
                    { giftCardGrant?.reward?.item_name }
                </H3>

                <Caption color={text}>
                    { t('giftCard.claimBefore', [dayjs(giftCardGrant?.expiration).format('MM/DD/YY')]) }
                </Caption>
            </Box>

            <MockGiftCard size="lg" amount={`$${giftCardGrant?.reward?.discount_value}`} />

            <FormControl isInvalid={claimGiftCard.isError}>
                <FormControl.ErrorMessage
                    marginBottom={2}
                    leftIcon={<WarningIcon size={5} />}
                >
                    { claimGiftCard.error?.data?.error }
                </FormControl.ErrorMessage>

                <Button
                    testID='alert-accept'
                    bg={gStyles.button_background}
                    _text={{
                        color: gStyles.button_text,
                    }}
                    disabled={claimGiftCard.isLoading}
                    onPress={handleClaimGiftCardPressed}
                >
                    { claimGiftCard.isLoading ? t('giftCard.claiming') : t('giftCard.claimNow') }
                </Button>
            </FormControl>
        </VStack>
    );

    const  successPanel = (
        <VStack space={4}>
            <H3 color={text}>
                { t('giftCard.claimSuccess', [merchant.gift_card_alias]) }
            </H3>

            <MockGiftCard size="lg" amount={`$${giftCardGrant?.reward?.discount_value}`} />

            <Button
                testID='alert-cancel'
                variant={'ghost'}
                bg={gStyles.button_neutral_background}
                whiteSpace={'nowrap'}
                _text={{
                    color: text,
                }}
                onPress={handleSuccessCloseModalPressed}
            >
                { t('redeem.go_back') }
            </Button>
        </VStack>
    );

    const [currentModalBody, setCurrentModalBody] = useState('claim-form');

    useEffect(() => {

        if (claimGiftCard.isSuccess) {
            setCurrentModalBody('success-panel');
        }
        else if (member?.consumer?.email_validated) {
            setCurrentModalBody('claim-form');
        }
        else {
            setCurrentModalBody('email-verify-form');
        }
    }, [member, claimGiftCard.isSuccess]);

    if (!giftCardGrant) {
        return null;
    }

    return (
        <Modal isOpen={true} overflowY={'auto'}>
            <Modal.Content>
                <Modal.CloseButton
                    bg={gStyles.button_neutral_background}
                    _icon={{
                        color: gStyles.button_neutral_text,
                    }}
                    onPress={handleSuccessCloseModalPressed}
                />
                <Modal.Body
                    bg={body_background}
                    textAlign={'center'}
                    h={'100%'}
                >
                    {
                        currentModalBody === 'email-verify-form' ? emailVerifyForm :
                            currentModalBody === 'claim-form' ? claimGiftCardForm :
                                currentModalBody === 'success-panel' ? successPanel : null
                    }
                </Modal.Body>
            </Modal.Content>
        </Modal>
    );
};

export default ClaimGiftCardModal;
